import { Link } from "react-router-dom";

import type { DashboardAssignedAudit } from "../../services/labService";
import { normalizeLabType } from "../../types/lab";
import { Card } from "../ui/Card";

interface AssignedAuditsPanelProps {
  assignedAudits: DashboardAssignedAudit[];
}

const formatAuditStateLabel = (value: string) =>
  value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const AssignedAuditsPanel = ({ assignedAudits }: AssignedAuditsPanelProps) => {
  if (assignedAudits.length === 0) {
    return null;
  }

  return (
    <Card variant="panel" className="p-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold">Assigned Audits</h2>
          <p className="text-base-content/70 text-sm">
            Certifications currently assigned to you for audit review.
          </p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm text-primary-content">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-content text-xs font-semibold md:h-8 md:w-8 shadow-md">
            {assignedAudits.length}
          </span>
          <span>Open audits</span>
        </span>
      </div>

      <div className="mt-5 space-y-4">
        {assignedAudits.map((audit) => (
          <Card
            key={audit.certification_id}
            variant="subtle"
            className="px-5 py-4 transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <div className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-base-content/55">
                    Audit
                  </p>
                  <h3 className="card-title mt-1 text-lg">
                    <Link
                      to={`/auditor/certification/${audit.certification_id}`}
                      className="truncate transition-colors hover:text-primary"
                    >
                      {audit.lab_name}
                    </Link>
                  </h3>
                  <p className="mt-1 text-sm capitalize text-base-content/70">
                    {normalizeLabType(audit.lab_type)} lab
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="badge badge-outline border-[#d8c8b0] bg-[#fff7df] text-base-content/80 capitalize">
                      {audit.certification_level}
                    </span>
                    <span className="badge badge-outline border-[#d8c8b0] bg-white text-base-content/80">
                      {formatAuditStateLabel(audit.certification_state)}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/auditor/certification/${audit.certification_id}`}
                  className="btn btn-primary btn-sm shrink-0 self-start sm:self-start"
                >
                  Open Audit
                </Link>
              </div>

              <div className="grid gap-2 border-t border-[#efe3d1] pt-4 text-sm text-base-content/75 sm:grid-cols-[minmax(0,1.4fr)_auto_auto] sm:items-center sm:gap-4">
                <p className="min-w-0">
                  <span className="font-medium text-base-content">Champion:</span>{" "}
                  <span className="break-all sm:break-normal">
                    {audit.champion_email ?? "No champion assigned"}
                  </span>
                </p>
                <p>
                  <span className="font-medium text-base-content">Started:</span>{" "}
                  {new Date(audit.started_at).toLocaleDateString()}
                </p>
                <p className="sm:text-right">
                  <span className="font-medium text-base-content">Checklist:</span>{" "}
                  {audit.auditor_checklist_submitted ? "Submitted" : "Ready for review"}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Card>
  );
};